import {
  Flex,
  FormControl,
  FormLabel,
  Image,
  Input as ChakraInput,
  InputGroup,
  InputProps as ChakraInputProps,
  Text,
  useBreakpointValue
} from "@chakra-ui/react";
import { forwardRef, ForwardRefRenderFunction, MouseEventHandler } from "react";
import { AiOutlineArrowRight } from "react-icons/ai";
import InputMask from "react-input-mask";

import { FieldErrors } from "react-hook-form";

import { BtnDefault } from "@/components/Button";

interface InputProps extends ChakraInputProps {
  width?: any;
  name: string;
  mask?: string;
  label?: string;
  error?: FieldErrors;
  placeholder?: string;
}

export function PrimaryInput({ placeholder, type }: InputProps) {
  return (
    <InputGroup>
      <ChakraInput
        type={type}
        placeholder={placeholder}
        borderRadius="0"
        errorBorderColor="red.900"
        focusBorderColor="green.900"
      />
    </InputGroup>
  );
}

interface inputLProps extends ChakraInputProps {
  label?: string;
  placeholder?: string;
  customPadding?: string;
  customWidth?: any;
  mask?: string;
  icon?: any;
  colorBg?: string;
  Number?: boolean;
  mt?: any;
}

export function InputWithLabel({
  placeholder,
  label,
  customWidth,
  mask,
  icon,
  colorBg,
  Number,
  mt,
  ...rest
}: inputLProps) {
  const isWideVersion = useBreakpointValue({
    base: false,
    xs: false,
    sm: false,
    md: false,
    lg: true,
    xl: true
  });
  return (
    <FormControl w={customWidth} mt={mt}>
      {label && (
        <FormLabel
          position="absolute"
          color="gray.400"
          fontSize={["text3", "text1"]}
          top="-10px"
          left="10px"
          backgroundColor="white"
          zIndex="2"
          p="0 5px"
        >
          {label}
        </FormLabel>
      )}
      {icon ? (
        <Flex
          w="auto"
          h={"40px"}
          border={"1px solid"}
          borderColor={"ll-gray.100"}
          bgColor={"ll-gray.600"}
          borderRadius={"50px"}
        >
          <ChakraInput
            variant={"link"}
            {...rest}
            h={"40px"}
            bgColor={"transparent"}
            outline={"none"}
            _focus={{ borderColor: "#1E1E1E", boxShadow: "none", outline: "none" }}
            _hover={{ borderColor: "#1E1E1E" }}
            as={InputMask}
            mask={mask}
            placeholder={placeholder}
            type={Number && "tel"}
          />
          <Image
            mr={"15px"}
            src={"https://cdn.pneufree.com.br/LINGLONG/images/Site/LingLong-icons/iconFreight.svg"}
            w={"35px"}
            loading="lazy"
            alt={"Alternative to Image"}
          />
        </Flex>
      ) : (
        <Flex
          w="auto"
          h={"40px"}
          border={"1px solid"}
          borderColor={"ll-gray.100"}
          bgColor={"white"}
          borderRadius={"50px"}
        >
          <ChakraInput
            variant={"link"}
            {...rest}
            h={"40px"}
            bgColor={"transparent"}
            outline={"none"}
            _focus={{ borderColor: "#1E1E1E", boxShadow: "none", outline: "none" }}
            _hover={{ borderColor: "#1E1E1E" }}
            as={InputMask}
            mask={mask}
            placeholder={placeholder}
            type={Number && "tel"}
          />
        </Flex>
      )}

      {/* {icon && (
        <Image
          src={
            "https://cdn.pneufree.com.br/LINGLONG/images/Site/LingLong-icons/ico-search-delivery.svg"
          }
          w={"30px"}
          loading="lazy"
          alt={"Alternative to Image"}
        />
      )}
      <ChakraInput
        {...rest}
        as={InputMask}
        mask={mask}
        bg={"ll-gray.600"}
        _placeholder={{ borderColor: "black" }}
        placeholder={placeholder}
        _focus={{ borderColor: "#1E1E1E", boxShadow: "none" }}
        _hover={{ borderColor: "#1E1E1E" }}
        type={Number && "tel"}
        fontSize={!isWideVersion && "16px"}
      /> */}
    </FormControl>
  );
}

export function InputWithoutLabel({
  placeholder,
  customPadding,
  customWidth,
  mask,
  ...rest
}: inputLProps) {
  const isWideVersion = useBreakpointValue({
    base: false,
    xs: false,
    sm: false,
    md: false,
    lg: true,
    xl: true
  });

  return (
    <ChakraInput
      {...rest}
      as={InputMask}
      p={customPadding ? customPadding : "25px 15px"}
      borderColor="gray.300"
      borderWidth="2px"
      borderRadius="5px"
      _placeholder={{ color: "gray.300" }}
      width={customWidth ? customWidth : "100%"}
      placeholder={placeholder}
      _hover={{ borderColor: "#1E1E1E" }}
      _focus={{ borderColor: "#1E1E1E" }}
      mask={mask}
      fontSize={!isWideVersion && "16px"}
    />
  );
}

interface InputSubmitProps extends ChakraInputProps {
  inputTitle: string;
  buttonTitle: string;
  placeholder: string;
  buttonAction: MouseEventHandler;
  loadingState: boolean;
  mask?: string;
}

export function InputSubmit({
  inputTitle,
  buttonTitle,
  placeholder,
  buttonAction,
  loadingState,
  mask,
  ...rest
}: InputSubmitProps) {
  const isWideVersion = useBreakpointValue({
    base: false,
    xs: false,
    sm: false,
    md: false,
    lg: true,
    xl: true
  });

  return (
    <Flex
      direction={["row", "column"]}
      mr={["10px", "0"]}
      mb={["10px", "0"]}
      width={["auto", "250px"]}
      alignItems={["center", "flex-start"]}
    >
      <Text fontSize="h3" mb={["0", "10px"]} mr={["10px", "0"]}>
        {inputTitle}
      </Text>
      <InputWithoutLabel
        {...rest}
        mb={["0", "10px"]}
        placeholder={placeholder}
        customPadding="15px"
        w={["200px", "250px"]}
        mask={mask}
      />
      <BtnDefault
        weightCustom="bold"
        width={!isWideVersion && "auto"}
        bgColor="green.900"
        _hover={{ bgColor: "green" }}
        ml="-2px"
        onClick={buttonAction}
        isLoading={loadingState}
      >
        {isWideVersion ? buttonTitle : <AiOutlineArrowRight fontSize="20px" />}
      </BtnDefault>
    </Flex>
  );
}

type CustomInputProps = ChakraInputProps & {
  width?: any;
  name: string;
  mask?: string;
  label?: string;
  error?: FieldErrors;
  placeholder?: string;
};

const InputBase: ForwardRefRenderFunction<HTMLInputElement, CustomInputProps> = (
  { width, name, mask, label, error = null, ...rest },
  ref
) => {
  return (
    <FormControl isInvalid={!!error} width={width}>
      {label && (
        <FormLabel
          htmlFor={name}
          position="absolute"
          color="gray.300"
          fontSize={["text3", "12px"]}
          top="-10px"
          left="10px"
          backgroundColor="white"
          zIndex="5"
          p="0 5px"
        >
          {label}
        </FormLabel>
      )}
      <ChakraInput
        // p={"25px 15px"}
        as={InputMask}
        mask={mask}
        id={name}
        height={"40px"}
        name={name}
        width={width}
        color="black"
        borderColor="gray.300"
        borderRadius="0"
        bgColor="white"
        variant="filled"
        _placeholder={{ color: "gray.300" }}
        _focus={{ borderColor: "black", boxShadow: "none" }}
        _hover={{ borderColor: "black" }}
        size="lg"
        ref={ref}
        {...rest}
      />
    </FormControl>
  );
};

export const CustomInput = (
  {
    name,
    mask,
    width,
    placeholder,
    type,
    fontSize,
    borderColor,
    needBorder,
    borderRadius,
    ...props
  }: {
    name: string;
    mask?: string;
    width?: any;
    placeholder?: string;
    type?: string;
    fontSize?: string;
    needBorder?: boolean;
  } & ChakraInputProps,
  ref = null
) => {
  return (
    <ChakraInput
      p={"25px 15px"}
      as={InputMask}
      mask={mask}
      type={type}
      height={props.height ?? "40px"}
      name={name}
      placeholder={placeholder}
      width={width}
      ref={ref.current}
      bgColor={"ll-gray.600"}
      fontSize={fontSize ? fontSize : "text1"}
      border="1px solid"
      borderColor="ll-gray.100"
      borderRadius={borderRadius ? borderRadius : "50px"}
      fontWeight={"600"}
      _placeholder={{ color: "gray.300" }}
      _hover={{ border: "1px solid #0C4EA2" }}
      _focus={{
        boxShadow: "none"
      }}
      size={props.size ?? "sm"}
      {...props}
    />
  );
};

export const Input = forwardRef(InputBase);
